import { ManagedImageEditRequest, getManagedImageOriginalFile, getManagedImagePendingEdits } from "./managed-image.types";

interface ManagedImageUploadMutation<TResponse> {
  (input: { id: string; data: { file: File } }): Promise<TResponse>;
}

interface ManagedImageEditMutation<TResponse> {
  (input: { id: string; data: ManagedImageEditRequest }): Promise<TResponse>;
}

interface PersistManagedImageDraftOptions<TUploadResponse, TEditResponse> {
  entityId: string;
  file?: File | null;
  uploadImage: ManagedImageUploadMutation<TUploadResponse>;
  applyImageEdit?: ManagedImageEditMutation<TEditResponse>;
  resolveEditEntityId?: (uploadResponse: TUploadResponse) => string;
}

/**
 * Persists a local managed-image draft after the parent entity has been saved.
 * The original upload is sent first, followed by the accumulated edit recipe.
 */
export const persistManagedImageDraft = async <TUploadResponse, TEditResponse>({
  entityId,
  file,
  uploadImage,
  applyImageEdit,
  resolveEditEntityId,
}: PersistManagedImageDraftOptions<TUploadResponse, TEditResponse>): Promise<void> => {
  if (!file) return;

  const originalFile = getManagedImageOriginalFile(file);
  const pendingEdits = getManagedImagePendingEdits(file);

  const uploadResponse = await uploadImage({
    id: entityId,
    data: { file: originalFile },
  });

  if (!pendingEdits || !applyImageEdit || (!pendingEdits.crop && !pendingEdits.removeBackground)) {
    return;
  }

  await applyImageEdit({
    id: resolveEditEntityId ? resolveEditEntityId(uploadResponse) : entityId,
    data: pendingEdits,
  });
};
